import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Building2, CalendarDays, FileSignature, Phone, AlertTriangle } from "lucide-react";
import PortalProposals from "./PortalProposals";
import PortalVisits from "./PortalVisits";
import PortalContracts from "./PortalContracts";

export default function ClientPortal() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/portal/${token}`);
            setData(r.data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Link non valido o scaduto");
        }
    }, [token]);
    useEffect(() => { load(); }, [load]);

    if (error) {
        return (
            <div className="min-h-screen pt-32 flex justify-center px-6">
                <div data-testid="portal-error" className="max-w-md w-full text-center border border-red-700/40 bg-red-900/20 p-8 h-fit">
                    <AlertTriangle size={28} className="text-red-300 mx-auto mb-3" />
                    <p className="text-red-200 text-sm">{error}</p>
                </div>
            </div>
        );
    }
    if (!data) return <div className="min-h-screen pt-32 text-center text-ink-400">Caricamento area personale...</div>;

    const toSign = (data.contracts || []).filter((c) => c.status === "sent").length;

    return (
        <div data-testid="client-portal" className="min-h-screen pt-28 pb-24 max-w-4xl mx-auto px-5 sm:px-8">
            {/* Header */}
            <div className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                    {data.agency?.logo && <img src={data.agency.logo} alt="" className="h-11 w-11 object-contain rounded-full border border-gold/30" />}
                    <div>
                        <div className="label-eyebrow">Area personale · {data.agency?.name}</div>
                        <h1 data-testid="portal-client-name" className="font-serif text-3xl sm:text-4xl text-ink-50 mt-1">Benvenuto, {data.client_name}</h1>
                    </div>
                </div>
                <p className="text-ink-400 text-sm">
                    Qui trovi gli immobili selezionati per te, le tue visite e i documenti da firmare.
                    {data.agency?.phone && (
                        <a href={`tel:${data.agency.phone}`} className="inline-flex items-center gap-1.5 text-gold hover:underline ml-2">
                            <Phone size={12} /> {data.agency.phone}
                        </a>
                    )}
                </p>
                {toSign > 0 && (
                    <div data-testid="portal-tosign-banner" className="mt-4 border border-gold/40 bg-gold/5 px-4 py-3 text-sm text-gold flex items-center gap-2">
                        <FileSignature size={15} /> Hai {toSign} document{toSign === 1 ? "o" : "i"} in attesa di firma — vai alla sezione Documenti qui sotto.
                    </div>
                )}
            </div>

            <Section icon={Building2} title="Immobili selezionati per te" count={(data.proposals || []).length}>
                <PortalProposals token={token} proposals={data.proposals || []} onChanged={load} />
            </Section>

            <Section icon={CalendarDays} title="Le tue visite" count={(data.appointments || []).length}>
                <PortalVisits token={token} appointments={data.appointments || []} onChanged={load} />
            </Section>

            <Section icon={FileSignature} title="Documenti e contratti" count={(data.contracts || []).length}>
                <PortalContracts token={token} contracts={data.contracts || []} onChanged={load} />
            </Section>

            <p className="text-[11px] text-ink-500 mt-12 text-center">
                Area personale riservata, fornita da {data.agency?.name} tramite vendidirect.it · Non condividere questo link.
            </p>
        </div>
    );
}

function Section({ icon: Icon, title, count, children }) {
    return (
        <div className="mb-12">
            <div className="flex items-center gap-2 mb-5">
                <Icon size={17} className="text-gold" />
                <h2 className="font-serif text-xl text-ink-50">{title}</h2>
                <span className="text-[10px] px-1.5 py-0.5 border border-white/15 text-ink-400">{count}</span>
            </div>
            {children}
        </div>
    );
}
